import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import SensorMenuOverlay from "../components/SensorMenuOverlay";
import AddSensorModal from "../components/AddSensorModal";
import EditSensorModal from "../components/EditSensorModal";
import "../style/SensoresPage.css";
import {
    Wifi,
    WifiOff,
    AlertTriangle,
    Activity,
    Search,
    MoreVertical,
    Plus,
} from "lucide-react";

const UNIDADES = {
    temperatura: "°C",
    humedad: "%",
    luz: "lx",
    ph: "pH",
};

export default function SensoresPage() {
    const [menuLateralAbierto, setMenuLateralAbierto] = useState(false);
    const [listaSensores, setListaSensores] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [textoBusqueda, setTextoBusqueda] = useState("");
    const [filtroTipo, setFiltroTipo] = useState("Todos");
    const [filtroEstado, setFiltroEstado] = useState("Todos");
    const [filtroTerreno, setFiltroTerreno] = useState("Todos");
    const [menuOpcionesAbierto, setMenuOpcionesAbierto] = useState(false);
    const [sensorSeleccionado, setSensorSeleccionado] = useState(null);
    const [modalAgregarAbierto, setModalAgregarAbierto] = useState(false);
    const [modalEditarAbierto, setModalEditarAbierto] = useState(false);

    const cargarSensores = async () => {
        setCargando(true);
        try {
            const token = localStorage.getItem("token");
            const res = await fetch(
                "https://agro-nexus-prod.vercel.app/api/usuario/sensores-datos",
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const json = await res.json();
            if (!json.success) {
                setListaSensores([]);
                setCargando(false);
                return;
            }
            const map = {};
            json.sensores.forEach((r) => {
                const id = r.sensor_id;
                if (!map[id]) {
                    map[id] = { ...r };
                } else if (
                    r.fecha_hora &&
                    new Date(r.fecha_hora) > new Date(map[id].fecha_hora)
                ) {
                    map[id] = { ...r };
                }
            });
            setListaSensores(Object.values(map));
        } catch (err) {
            setListaSensores([]);
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargarSensores();
        const intervalo = setInterval(cargarSensores, 30000);
        return () => clearInterval(intervalo);
    }, []);

    const eliminarSensor = async (id) => {
        if (!window.confirm("¿Seguro que quieres eliminar este sensor?")) return;

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`https://agro-nexus-prod.vercel.app/api/sensores/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });

            const data = await res.json();
            if (res.ok) {
                alert("✅ Sensor eliminado correctamente");
                setListaSensores((prev) => prev.filter((s) => s.sensor_id !== id));
                setMenuOpcionesAbierto(false);
            } else {
                alert(`⚠️ Error: ${data.message}`);
            }
        } catch (error) {
            console.error("❌ Error eliminando sensor:", error);
            alert("Error eliminando el sensor.");
        }
    };

    const sensoresFiltrados = listaSensores.filter((s) => {
        const q = textoBusqueda.trim().toLowerCase();
        if (
            q &&
            !(s.sensor?.toLowerCase().includes(q) || s.terreno?.toLowerCase().includes(q))
        )
            return false;
        if (filtroTipo !== "Todos" && s.tipo_sensor !== filtroTipo) return false;
        if (
            filtroEstado !== "Todos" &&
            s.estado?.toLowerCase() !== filtroEstado.toLowerCase()
        )
            return false;
        if (filtroTerreno !== "Todos" && s.terreno !== filtroTerreno) return false;
        return true;
    });

    const totalRegistrados = listaSensores.length;
    const totalActivos = listaSensores.filter((s) => s.estado === "activo").length;
    const totalInactivos = listaSensores.filter((s) => s.estado === "inactivo").length;
    const totalAlertas = listaSensores.filter((s) => {
        if (s.valor === null || s.valor === undefined) return false;
        const v = parseFloat(s.valor);
        if (isNaN(v)) return false;
        const min = s.valor_minimo !== null ? parseFloat(s.valor_minimo) : null;
        const max = s.valor_maximo !== null ? parseFloat(s.valor_maximo) : null;
        if (min !== null && v < min) return true;
        if (max !== null && v > max) return true;
        return false;
    }).length;

    const terrenos = Array.from(
        new Set(listaSensores.map((s) => s.terreno).filter(Boolean))
    );

    return (
        <div className="contenedor-panel contenedor-oscuro">
            <div className="contenedor-lateral">
                <Sidebar isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
            </div>
            <div className="contenedor-principal">
                <Header isOpen={menuLateralAbierto} setIsOpen={setMenuLateralAbierto} />
                <div className="contenedor-informacion">
                    {/* RESUMEN */}
                    <div className="panel-resumen-sensores">
                        <div className="tarjeta-resumen">
                            <div className="tarjeta-resumen-cabecera">
                                <Activity size={20} />
                                <span className="tarjeta-resumen-titulo">Total Sensores</span>
                            </div>
                            <div className="tarjeta-resumen-valor">{totalRegistrados}</div>
                            <div className="tarjeta-resumen-subtitulo">Registrados</div>
                        </div>
                        <div className="tarjeta-resumen">
                            <div className="tarjeta-resumen-cabecera">
                                <Wifi size={20} />
                                <span className="tarjeta-resumen-titulo">Activos</span>
                            </div>
                            <div className="tarjeta-resumen-valor texto-verde">
                                {totalActivos}
                            </div>
                            <div className="tarjeta-resumen-subtitulo">Funcionando</div>
                        </div>
                        <div className="tarjeta-resumen">
                            <div className="tarjeta-resumen-cabecera">
                                <WifiOff size={20} />
                                <span className="tarjeta-resumen-titulo">Inactivos</span>
                            </div>
                            <div className="tarjeta-resumen-valor texto-rojo">
                                {totalInactivos}
                            </div>
                            <div className="tarjeta-resumen-subtitulo">Sin conexión</div>
                        </div>
                        <div className="tarjeta-resumen">
                            <div className="tarjeta-resumen-cabecera">
                                <AlertTriangle size={20} />
                                <span className="tarjeta-resumen-titulo">Alertas</span>
                            </div>
                            <div className="tarjeta-resumen-valor texto-amarillo">
                                {totalAlertas}
                            </div>
                            <div className="tarjeta-resumen-subtitulo">Requieren atención</div>
                        </div>
                    </div>

                    <div className="tarjeta-lista-sensores">
                        <div className="cabecera-lista-sensores">
                            <h3>Lista de Sensores</h3>
                            <button
                                className="boton-agregar-sensor"
                                onClick={() => setModalAgregarAbierto(true)}
                            >
                                <Plus size={18} />
                            </button>
                        </div>

                        <div className="filtros-sensores">
                            <div className="caja-busqueda-sensores">
                                <Search size={18} />
                                <input
                                    placeholder="Buscar por nombre o ubicación..."
                                    value={textoBusqueda}
                                    onChange={(e) => setTextoBusqueda(e.target.value)}
                                />
                            </div>
                            <select
                                value={filtroTipo}
                                onChange={(e) => setFiltroTipo(e.target.value)}
                            >
                                <option value="Todos">Todos los tipos</option>
                                {Array.from(new Set(listaSensores.map((s) => s.tipo_sensor))).map(
                                    (t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    )
                                )}
                            </select>
                            <select
                                value={filtroEstado}
                                onChange={(e) => setFiltroEstado(e.target.value)}
                            >
                                <option value="Todos">Todos los estados</option>
                                <option value="activo">Activos</option>
                                <option value="inactivo">Inactivos</option>
                            </select>
                            <select
                                value={filtroTerreno}
                                onChange={(e) => setFiltroTerreno(e.target.value)}
                            >
                                <option value="Todos">Todos los terrenos</option>
                                {terrenos.map((t) => (
                                    <option key={t} value={t}>
                                        {t}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="lista-sensores">
                            {cargando ? (
                                <div className="mensaje-cargando">Cargando sensores...</div>
                            ) : sensoresFiltrados.length === 0 ? (
                                <div className="mensaje-sin-datos">
                                    No se encontraron sensores
                                </div>
                            ) : (
                                sensoresFiltrados.map((s) => {
                                    const valor = s.valor ?? "--";
                                    const unidad =
                                        UNIDADES[s.tipo_sensor?.toLowerCase()] || "";
                                    const fueraRango =
                                        s.valor !== null &&
                                        s.valor !== undefined &&
                                        !isNaN(parseFloat(s.valor)) &&
                                        ((s.valor_minimo !== null &&
                                            parseFloat(s.valor) < parseFloat(s.valor_minimo)) ||
                                            (s.valor_maximo !== null &&
                                                parseFloat(s.valor) > parseFloat(s.valor_maximo)));

                                    return (
                                        <div key={s.sensor_id} className="fila-sensor">
                                            <div className="lado-izquierdo-sensor">
                                                <span
                                                    className={`punto-estado ${s.estado === "activo"
                                                        ? "punto-verde"
                                                        : "punto-rojo"
                                                        }`}
                                                />
                                                <div className="informacion-sensor">
                                                    <div className="nombre-sensor">{s.sensor}</div>
                                                    <div className="meta-sensor">
                                                        {s.terreno}
                                                        <span className="etiqueta-sensor">
                                                            {s.tipo_sensor}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="lado-derecho-sensor">
                                                <div className="valor-sensor">
                                                    {valor}
                                                    <span className="unidad-sensor">{unidad}</span>
                                                    {fueraRango && (
                                                        <span className="etiqueta-alerta">⚠</span>
                                                    )}
                                                </div>
                                                <div className="fecha-sensor">
                                                    {s.fecha_hora
                                                        ? new Date(s.fecha_hora).toLocaleString()
                                                        : "Sin datos"}
                                                </div>
                                                <div
                                                    className={`chip-estado ${s.estado === "activo" ? "activo" : "inactivo"
                                                        }`}
                                                >
                                                    {s.estado === "activo" ? "Activo" : "Inactivo"}
                                                </div>
                                                <button
                                                    className="boton-menu-sensor"
                                                    onClick={() => {
                                                        setSensorSeleccionado(s);
                                                        setMenuOpcionesAbierto(true);
                                                    }}
                                                >
                                                    <MoreVertical size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <SensorMenuOverlay
                open={menuOpcionesAbierto}
                onClose={() => setMenuOpcionesAbierto(false)}
                sensor={sensorSeleccionado}
                onDelete={(id) => {
                    setListaSensores((prev) => prev.filter((s) => s.sensor_id !== id));
                }}
                onEdit={() => {
                    setModalEditarAbierto(true);
                    setMenuOpcionesAbierto(false);
                }}
            />

            <AddSensorModal
                open={modalAgregarAbierto}
                onClose={() => setModalAgregarAbierto(false)}
                onAdd={cargarSensores}
            />

            <EditSensorModal
                open={modalEditarAbierto}
                onClose={() => setModalEditarAbierto(false)}
                sensor={sensorSeleccionado}
                onEdit={cargarSensores}
            />
        </div>
    );
}
